import Route from '@ember/routing/route'
import { service } from '@ember/service'
import { isSubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { hasMetadataRecord } from 'teacher-ui-client/utils/routing'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type RouterService from '@ember/routing/router-service'
import type Transition from '@ember/routing/transition'
import type SubscriptionUtilisation from 'teacher-ui-client/models/subscription-utilisation'
import type SchoolSubscription from 'teacher-ui-client/models/school-subscription'
import type TeacherSubscription from 'teacher-ui-client/models/teacher-subscription'
import type ApplicationRoute from 'teacher-ui-client/application/route'
import type SideNavService from 'teacher-ui-client/services/side-nav'

/**
 * Extract the subscription_type param out of the transition destination if it exists.
 * Run the result through a type guard to ensure it actually is a valid subscription type.
 */
export function extractSubscriptionType(transition: Transition): unknown {
  const toRouteInfo = transition.to?.find((routeInfo) => Boolean(routeInfo?.params?.subscription_type))
  return toRouteInfo?.params?.subscription_type
}

export default class SubscriptionTypeRoute extends Route {
  @service router!: RouterService
  @service sideNav!: SideNavService

  /**
   * Uses a transition to determine if the destination requires authorization of the users' subscriptions.
   */
  needsAuthorizedSubscription(transition: Transition): boolean {
    const toSubscriptionTypeArea = transition.to?.find((routeInfo) => routeInfo.name === this.routeName)
    const skipAuthCheck = transition.to?.find(
      (routeInfo) => hasMetadataRecord(routeInfo) && Boolean(routeInfo.metadata.allowUnauthorizedSubscription),
    )
    return Boolean(toSubscriptionTypeArea && !skipAuthCheck)
  }

  /**
   * Authorize that a users' subscriptions permit them to enter the subscription type area.
   *
   * If unauthorized, user will be redirected to access-missing or access-expired pages.
   */
  authorizeSubscription = (
    subscriptionType: SubscriptionType,
    utilisations: SubscriptionUtilisation[],
    schoolSubs: SchoolSubscription[],
    teacherSubs: TeacherSubscription[],
  ) => {
    // Only care about utilisations and subs for this subscription type
    const utilisationsForType = utilisations.filter((utilisation) => utilisation.subscriptionType === subscriptionType)
    const schoolSubsForType = schoolSubs.filter((sub) => sub.subscriptionType === subscriptionType)
    const teacherSubsForType = teacherSubs.filter((sub) => sub.subscriptionType === subscriptionType)

    const hasUtilisationsForType = utilisationsForType.length > 0
    const hasSchoolSubsForType = schoolSubsForType.length > 0
    const hasTeacherSubsForType = teacherSubsForType.length > 0

    // if they have utilisations for this subtype, proceed. Otherwise, figure out which access page to send them to
    if (!hasUtilisationsForType) {
      if (!hasSchoolSubsForType && !hasTeacherSubsForType) {
        // access missing if no subscriptions for this sub type. no school sub, no teacher sub.
        void this.router.replaceWith('subscription-type.access-missing')
      } else if ([...schoolSubsForType, ...teacherSubsForType].some((sub) => sub.isActive)) {
        // no utilisation records, but somehow has an active sub. should not happen.
        throw new Error('no utilisations found for active subscriptions')
      } else if ([...schoolSubsForType, ...teacherSubsForType].every((sub) => sub.isPending)) {
        // access missing if all present subs haven't started yet
        void this.router.replaceWith('subscription-type.access-missing')
      } else if ([...schoolSubsForType, ...teacherSubsForType].every((sub) => sub.isExpired)) {
        // access expired if all present subs have expired
        void this.router.replaceWith('subscription-type.access-expired')
      } else {
        // subs present must be a mix of both expired and subs that haven't started yet
        void this.router.replaceWith('subscription-type.access-expired')
      }
    }
  }

  override activate() {
    this.router.on('routeWillChange', this.checkSubscriptionType)
  }

  override deactivate() {
    this.router.off('routeWillChange', this.checkSubscriptionType)
  }

  /**
   * In the case of an invalid or unavailable subscriptionType, it will transition to the landing page, which will
   * determine what to do about it.
   */
  checkSubscriptionType = (transition: Transition) => {
    // Authorize any transitions within the subscription-type area against the users' subscriptions.
    if (this.needsAuthorizedSubscription(transition)) {
      // Verify the subscription type is still valid before using it for any authorization
      const subscriptionType = extractSubscriptionType(transition)
      if (!isSubscriptionType(subscriptionType)) {
        // Take them to landing in case they have a valid sub type they can be redirected to.
        void this.router.replaceWith('landing')
        return
      }

      const { subscriptionUtilisations, schoolSubscriptions, teacherSubscriptions } = this.modelFor(
        'application',
      ) as ModelFor<ApplicationRoute>
      return this.authorizeSubscription(
        subscriptionType,
        subscriptionUtilisations,
        schoolSubscriptions,
        teacherSubscriptions,
      )
    }
  }

  beforeModel(transition: Transition) {
    return this.checkSubscriptionType(transition)
  }

  /**
   * Returns commonly used data that is relevant to the subscription type, or used by the subscription-type controller.
   *
   * For the param args, we can assume the SubscriptionType type instead of string, because we've already validated the
   * subscriptionType in the beforeModel.
   */
  model({ subscription_type: subscriptionType }: { subscription_type: SubscriptionType }) {
    const { subscriptionUtilisations: utilisations, schoolStatistics } = this.modelFor(
      'application',
    ) as ModelFor<ApplicationRoute>
    const subscriptionUtilisation = utilisations.find((util) => util.subscriptionType === subscriptionType)

    return {
      subscriptionType,
      subscriptionUtilisation,
      schoolStatistics,
    }
  }

  afterModel(resolvedModel: ModelFor<SubscriptionTypeRoute>) {
    this.sideNav.updateState(resolvedModel)
  }
}
